import axios from 'axios';
import { find as _find } from 'lodash';
import moment from 'moment';

import otaForceDownload from '../../components/otaForceDownload';
import serializeQueryParams from '../../helpers/serializeQueryParams';
import { SCOPE } from '../../report/insights/constants/scopes';
import emptyPerformance from '../../report/insights/performance/constants/emptyPerformanceChartData';
import type {
  DateRange,
  EmailReportsSchedule,
  InsightsPassCount,
  InsightsStatistics,
  SiteStatistics,
} from './reporting.types';

function passInsightsResponse(passCount: Array<InsightsPassCount> = [], responses: Array<InsightsPassCount> = []) {
  responses.forEach((response) => {
    const pass = _find(passCount, { name: response.name });
    if (!pass) {
      passCount.push(response);
    } else {
      pass.autoExpired += response.autoExpired;
      pass.count += response.count;
    }
  });

  return passCount;
}

function getDateRangeForPeriod(periodValue: number) {
  let dateRange = [[periodValue, 0]];
  if (periodValue === 31) {
    dateRange = [
      [7, 0],
      [14, 7],
      [21, 14],
      [28, 21],
      [31, 28],
    ];
  }

  const now = moment().toISOString();

  return dateRange.map(([startDate, endDate]) => {
    return {
      startDate: moment(now).subtract(startDate, 'day').toISOString(),
      endDate: moment(now).subtract(endDate, 'day').toISOString(),
    };
  });
}

function aggregateStats(stats: InsightsStatistics, fetchedStats: InsightsStatistics) {
  return {
    ...stats,
    active: fetchedStats.active || stats.active,
    pending: fetchedStats.pending || stats.pending,
    autoExpired: stats.autoExpired + fetchedStats.autoExpired,
    expired: stats.expired + fetchedStats.expired,
    rejected: stats.rejected + fetchedStats.rejected,
    total: stats.total + fetchedStats.total,
  };
}

async function fetchInsightsStatistics(siteIds: Array<string>, dateRange = {}, includeActive = false) {
  const body = {
    siteIds,
    dateRange,
  };

  const activeStatsPromise = includeActive
    ? axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/statistics/active`, { siteIds })
    : Promise.resolve(null);
  const [activeStats, totalStats, expiredStats] = await Promise.all([
    activeStatsPromise,
    axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/statistics/total`, body),
    axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/statistics/expired`, body),
  ]);

  return {
    total: totalStats.data?.total || 0,
    active: includeActive ? activeStats?.data?.active || 0 : null,
    pending: includeActive ? activeStats?.data?.pending || 0 : null,
    rejected: expiredStats.data?.rejected || 0,
    autoExpired: expiredStats.data?.autoExpired || 0,
    expired: expiredStats.data?.expired || 0,
  };
}

export default {
  async getTimeSeriesByStatus(siteIds: Array<string>, interval: string, steps: number, utcOffset: number) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/trends/by-status`;

    const params = {
      siteIds,
      interval,
      steps,
      utcOffset,
    };

    const resp = await axios.post(url, params);
    return resp.data;
  },

  async getTimeSeriesForStatusesBySite(
    siteIds: Array<string>,
    interval: string,
    steps: number,
    utcOffset: number,
    statuses: Array<string>,
  ) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/trends/by-site`;

    const params = {
      siteIds,
      interval,
      steps,
      utcOffset,
      statuses,
    };

    const resp = await axios.post(url, params);
    return resp.data;
  },

  async getTimeSeriesForStatusesByVisitorType(
    siteIds: Array<string>,
    interval: string,
    steps: number,
    utcOffset: number,
    statuses: Array<string>,
  ) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/trends/by-visitor-type`;

    const params = {
      siteIds,
      interval,
      steps,
      utcOffset,
      statuses,
    };

    const resp = await axios.post(url, params);
    return resp.data;
  },

  async getPassesFromPeriod(siteIds: Array<string>, period: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/pass-count/from-period`;

    const params = {
      siteIds,
      period,
    };

    const resp = await axios.post(url, params);
    return resp.data;
  },

  async setEmailReportsSchedule(params: Record<string, any>, teamId: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/scheduling/email-reports`;

    // https://sinehq.atlassian.net/browse/PLAT-1634
    // todo: this is manually put in till further work done on team interceptor
    const config = {
      headers: {
        'x-sine-team-id': teamId,
      },
    };

    const resp = await axios.post(url, params, config);
    return resp.data;
  },

  async getEmailReportsSchedule(teamId: string) {
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/scheduling/email-reports`;

    // https://sinehq.atlassian.net/browse/PLAT-1634
    // todo: this is manually put in till further work done on team interceptor
    const config = {
      headers: {
        'x-sine-team-id': teamId,
      },
    };

    const resp = await axios.get<EmailReportsSchedule>(url, config);
    return resp.data;
  },

  async getSiteStatistics(params: Record<string, any>) {
    const qs = serializeQueryParams(params);
    const url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/site-statistics?${qs}`;

    const resp = await axios.get<Array<SiteStatistics>>(url);
    return resp.data;
  },

  exportSiteList(params: { teamId: string }) {
    const qs = serializeQueryParams(params);
    return otaForceDownload.download(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/site-list-export?${qs}`,
      'download.csv',
    );
  },

  exportHostList(params: {
    teamId: string;
    siteId: string | null; // null means all sites
  }) {
    const qs = serializeQueryParams(params);
    return otaForceDownload.download(`${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/host-list-export?${qs}`);
  },

  exportPasses(exportContext: string, params: Record<string, any>) {
    let url = '';
    if (exportContext === 'mypasses') {
      const qs = serializeQueryParams(params);
      url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/mypass-export?${qs}`;
      return axios.get(url);
    }

    url = `${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/pass-export`;
    return axios.post(url, params);
  },

  downloadFile(fileId: string) {
    return otaForceDownload.downloadAWS(
      `${window.env.REACT_APP_SINE_CORE_API_URI}/file/download/${fileId}?shouldReturnLink=true`,
    );
  },

  async getInsightsStatistics(siteIds: Array<string>, periodValue: number) {
    const dateRange = getDateRangeForPeriod(periodValue);
    let stats = emptyPerformance;

    for (let i = 0; i < dateRange.length; i++) {
      const includeActive = i === 0;
      const fetchedStats = await fetchInsightsStatistics(siteIds, dateRange[i], includeActive);
      stats = aggregateStats(stats, fetchedStats);
    }

    return stats;
  },

  async fetchInsightsPassCountBy(
    siteIds: Array<string>,
    dateRange: DateRange | undefined,
    scope: string,
    statuses: Array<string>,
  ) {
    const { data } = await axios.post(`${window.env.REACT_APP_SINE_CORE_API_URI}/reporting/insights/pass-count-by`, {
      siteIds,
      scope,
      statuses,
      dateRange,
    });

    return data;
  },

  async getInsightsPassCountBy(siteIds: Array<string>, statuses: Array<string>, periodValue: number) {
    const dateRange = periodValue ? getDateRangeForPeriod(periodValue) : [undefined];
    let visitorData = passInsightsResponse();
    let deviceData = passInsightsResponse();

    for (let i = 0; i < dateRange.length; i++) {
      const [visitorDataForRange, deviceDataForRange] = await Promise.all([
        this.fetchInsightsPassCountBy(siteIds, dateRange[i], SCOPE.VISITOR_TYPE, statuses),
        this.fetchInsightsPassCountBy(siteIds, dateRange[i], SCOPE.DEVICE_TYPE, statuses),
      ]);

      visitorData = passInsightsResponse(visitorDataForRange, visitorData);
      deviceData = passInsightsResponse(deviceDataForRange, deviceData);
    }

    return { visitorData, deviceData };
  },
};
